/* eslint-disable max-lines-per-function */
import React from 'react'
import { TagAxisId, tagAxisIds, tagAxesObject, DetailedTag, tagMap, getAllParentsTagIds } from '@cpsy-rmap-project/tags'
import { Paper, DetailedPaper } from '@cpsy-rmap-project/web-core/repositories'
import { query, collection } from 'firebase/firestore'
// import produce from 'immer'
import _, { isNil, flatMap, intersection } from 'lodash'
import { useFirestoreCollection, useFirestore } from 'reactfire'
import { LoadingScreen } from '../LoadingScreen'

type PaperTagData = { tagIds: string[]; tagNames: string }

const getTags = (paper: Paper, tagAxisId: TagAxisId) => {
  const depth = tagAxesObject[tagAxisId].enableToInclueOtherTagToTopLevel
    ? tagAxesObject[tagAxisId].depth - 1
    : tagAxesObject[tagAxisId].depth
  const tags: Array<PaperTagData & { tagNamesTmp: string[] }> = new Array(depth).fill(undefined).map(() => ({
    tagIds: [],
    tagNamesTmp: [],
    tagNames: ''
  }))
  const pushTags = (tag: DetailedTag) => {
    // NOTE：enableToInclueOtherTagToTopLevelフラグがついている場合は全て深さ2を1に揃えるものとする
    const tagDepth = tagAxesObject[tagAxisId].enableToInclueOtherTagToTopLevel ? 0 : tag.depth - 1

    // NOTE: enableToInclueOtherTagToTopLevelフラグがついている場合はタグOtherはpushしない
    if (tagAxesObject[tagAxisId].enableToInclueOtherTagToTopLevel) {
      if (tag.name !== 'Others') {
        tags[tagDepth].tagIds.push(tag.id)
        tags[tagDepth].tagNamesTmp.push(tag.name)
      }
    } else {
      tags[tagDepth].tagIds.push(tag.id)
      tags[tagDepth].tagNamesTmp.push(tag.name)
    }

    if (tag.parentTagId) {
      const parentTag = tagMap[tag.parentTagId]
      parentTag && pushTags(parentTag)
    }
  }
  paper[tagAxisId].forEach(tagId => {
    const detailedTag = tagMap[tagId]
    detailedTag && pushTags(detailedTag)
  })

  return tags.map(t => ({
    tagIds: _.uniq(t.tagIds), // 親IDの重複を解消する
    tagNames: t.tagNamesTmp.join(', ') // すべてのタグをジョインする
    // tagNames: t.tagNamesTmp.length > 0 ? t.tagNamesTmp[0] : '' // 一番目のタグのみを表示する
  }))
}

/** ドラフトフラグ検索用のPaperIDリストを作る */
type DraftStatusFilterdType = {
  draft: string[]
  registration: string[]
}

/** 全タグIDの紐づくIDリストを作る */
type EachTagIndexes = {
  [x: string /** 全タグID */]: string[]
}

/** 最上位親タグID x 最上位親タグIDの紐づくIDリストを作る */
type AncestorTag2DIndexes = {
  [x: string /** 最上位親タグID */]: {
    [y: string /** 最上位親タグID */]: string[]
  }
}

/** papers用のMAP */
type PapersMap = Map<string /** firestoreのpapers.id */, DetailedPaper>

export interface PaperProps {
  papers: DetailedPaper[]
  outsidePapers: DetailedPaper[]
  papersMap: PapersMap
  forFilter: {
    // 事前index系
    eachTagIndexes: EachTagIndexes
    ancestorTag2DIndexes: AncestorTag2DIndexes

    // データ事前処理系
    draftStatusFilterdPaperIds: DraftStatusFilterdType
  }
  // handler: {
  //   create: (paper: Paper) => void
  //   edit: (paper: Paper) => void
  //   delete: (paper: Pick<Paper, 'id'>) => void
  // }
  // handleRefetchAllPapers: () => void
}

export const initPaperProps: PaperProps = {
  papers: [],
  outsidePapers: [],
  papersMap: new Map<string, DetailedPaper>(),
  forFilter: {
    eachTagIndexes: {},
    ancestorTag2DIndexes: {},
    draftStatusFilterdPaperIds: {
      draft: [],
      registration: []
    }
  }
  // handler: {
  //   create: () => {},
  //   edit: () => {},
  //   delete: () => {}
  // }
  // handleRefetchAllPapers: () => {}
}

const PaperContext = React.createContext<PaperProps>(initPaperProps)

export const PaperProvider: React.FC = ({ children }) => {
  // Firebase
  const db = useFirestore()
  // const allPapersCompressedRef = React.useMemo(() => firebase.storage().ref('allPapers.compressed'), [firebase])

  const [isInitLoading, setIsInitLoading] = React.useState(true)
  const [papers, setPapers] = React.useState<Paper[]>([])
  const [detailedPapers, setDetailedPapers] = React.useState<DetailedPaper[]>([])

  const [outsidePapers, setOutsidePapers] = React.useState<Paper[]>([])
  const [detailedOutsidePapers, setDetailedOutsidePapers] = React.useState<DetailedPaper[]>([])

  const [papersMap, setPapersMap] = React.useState<PapersMap>(new Map<string, DetailedPaper>())
  const [eachTagIndexes, setEachTagIndexes] = React.useState<EachTagIndexes>({})
  const [ancestorTag2DIndexes, setAncestorTag2DIndexes] = React.useState<AncestorTag2DIndexes>({})
  const [draftStatusFilterdPaperIds, setDraftStatusFilterdPaperIds] = React.useState<DraftStatusFilterdType>({
    draft: [],
    registration: []
  })

  // /**
  //  * DBデータ取得（直接DBからデータを取得する場合）
  //  */

  const { status, data } = useFirestoreCollection(
    query(
      collection(db, 'papers')
      // orderBy('citedCount', 'desc') // NOTE: 被引用数でソートを事前にしておく ← 2021/11/10修正（citedCountが存在しないPaperが表示されなくなってしまう）
    )
  )

  React.useEffect(() => {
    console.log('[debug] [PaperProvider] useEffect')
    if (status === 'success') {
      console.log('🕑 set DB data all Papers')
      const allPapers = (
        (data.docs.map(s => ({
          ...s.data(),
          id: s.id // NOTE: IDを足している
        })) ?? []) as Paper[]
      ).sort(
        // NOTE: 被引用数でソートを事前にしておく
        (a, b) => {
          if (!a.citedCount) return 1
          if (!b.citedCount) return -1
          if (a.citedCount >= b.citedCount) return -1
          return 1
        }
      )
      setPapers(allPapers.filter(p => p.outside !== true))
      setOutsidePapers(allPapers.filter(p => p.outside === true))
      setIsInitLoading(false)
    }
  }, [status, data])

  /**
   * 圧縮JSONからDB取得（使っていない）
   */
  // const [allPapersJsonUpdatedAt, setAllPapersJsonUpdatedAt] = React.useState<Date>()
  // const [refetching, setRefetching] = React.useState(true)
  // const [isUpdatedServerData, setIsUpdatedServerData] = React.useState(true)
  // const handleRefetchAllPapers = React.useCallback(() => {
  //   setRefetching(true)
  //   setIsUpdatedServerData(false)
  // }, [])

  /**
   * JSONデータ取得初回ロード
   * FIXME: TOPページの更新がかからない
   * FIXME: firestore data bundlerを使う方がよい
   */
  // React.useEffect(() => {
  //   const fn = async () => {
  //     console.log('🐾 Loading All Papers Json file')
  //     const allPapersJsonUrl = await allPapersCompressedRef.getDownloadURL()

  //     // FIXME: 🔥🔥🔥🔥 CORS設定のスクリプト化 gsutil cors set cors.json gs://ncnp-cpsy-rmap-development.appspot.com （https://firebase.google.com/docs/storage/web/download-files?hl=ja ）

  //     const data = await fetch(allPapersJsonUrl) // FIXME: 🔥 キャッシュ
  //     if (data.status === 404) {
  //       throw new Error('404')
  //     }
  //     const jsonData = JSON.parse(
  //       pako.inflateRaw(new Uint8Array(await data.arrayBuffer()), {
  //         to: 'string'
  //       })
  //     )
  //     setPapers(jsonData as any)
  //     setIsInitLoading(false)
  //     console.log('🕑 Loaded all papers data')
  //   }
  //   fn()
  // }, [allPapersCompressedRef, db, firebase])

  /**
   * papersが更新されたら
   * NOTE: パフォーマンスによってはJSONデータに格納してもよい
   */
  React.useEffect(() => {
    console.log('😺 Papersが更新されました', papers)
    const _detailedPapers: DetailedPaper[] = []
    const _eachTagIndexes: EachTagIndexes = {}
    const _ancestorTag2DIndexes: AncestorTag2DIndexes = {}
    const _draftStatusFilterdPaperIds: DraftStatusFilterdType = { draft: [], registration: [] }
    const _papersMap = new Map<string, DetailedPaper>()

    for (const p of papers) {
      // タグ名を補完する
      const detailedPaper: DetailedPaper = {
        ...p,
        details: {
          rdocConstructs: getTags(p, 'rdocConstructs'),
          unitOfAnalysis: getTags(p, 'unitOfAnalysis'),
          dsm5: getTags(p, 'dsm5'),
          compMathMethodExperimentalDesign: getTags(p, 'compMathMethodExperimentalDesign'),
          compMathMethodDataType: getTags(p, 'compMathMethodDataType'),
          compMathMethodModels: getTags(p, 'compMathMethodModels'),
          symptomatology: getTags(p, 'symptomatology')
        }
      }
      _detailedPapers.push(detailedPaper)

      // タグ→最上位親タグのID毎にindex番号をpushする
      for (const tagAxidId of tagAxisIds) {
        // 関連する親タグも含めて精査する
        for (const tagId of getAllParentsTagIds(p[tagAxidId])) {
          // eachTagIndexes更新
          if (isNil(_eachTagIndexes[tagId])) {
            _eachTagIndexes[tagId] = [p.id]
          } else {
            _eachTagIndexes[tagId].push(p.id)
          }
        }
      }

      // mapに格納
      _papersMap.set(p.id, detailedPaper)

      // ドラフトStatusを追記
      if (p.draftByAutoTag === true) {
        _draftStatusFilterdPaperIds.draft.push(p.id)
      } else {
        _draftStatusFilterdPaperIds.registration.push(p.id)
      }
    }

    // 最上位タグのみのIndexを作る
    const ancestorTagIds = flatMap(tagAxisIds, tagAxisId => tagAxesObject[tagAxisId].tags.map(tag => tag.id))
    for (const x of ancestorTagIds) {
      for (const y of ancestorTagIds) {
        if (isNil(_ancestorTag2DIndexes[x])) {
          _ancestorTag2DIndexes[x] = {}
        }
        _ancestorTag2DIndexes[x][y] = intersection(_eachTagIndexes[x], _eachTagIndexes[y])
      }
    }

    setDetailedPapers(_detailedPapers)
    setEachTagIndexes(_eachTagIndexes)
    setAncestorTag2DIndexes(_ancestorTag2DIndexes)
    setDraftStatusFilterdPaperIds(_draftStatusFilterdPaperIds)
    setPapersMap(_papersMap)
  }, [papers])

  /**
   * outsidePapersが更新されたら
   */
  React.useEffect(() => {
    console.log('🐷 outsidePapersが更新されました', outsidePapers)
    const _detailedPapers: DetailedPaper[] = []
    for (const p of outsidePapers) {
      // タグ名を補完する
      const detailedPaper: DetailedPaper = {
        ...p,
        details: {
          rdocConstructs: getTags(p, 'rdocConstructs'),
          unitOfAnalysis: getTags(p, 'unitOfAnalysis'),
          dsm5: getTags(p, 'dsm5'),
          compMathMethodExperimentalDesign: getTags(p, 'compMathMethodExperimentalDesign'),
          compMathMethodDataType: getTags(p, 'compMathMethodDataType'),
          compMathMethodModels: getTags(p, 'compMathMethodModels'),
          symptomatology: getTags(p, 'symptomatology')
        }
      }
      _detailedPapers.push(detailedPaper)
    }

    setDetailedOutsidePapers(_detailedPapers)
  }, [outsidePapers])

  /** 更新操作系のhandler（圧縮JSONからDB取得の場合は大量データのせいでリアルタイム更新が使えないので） */
  // const handler: PaperProps['handler'] = {
  //   create: createdPaper => {
  //     console.log('Paper Created ->', createdPaper)
  //     const nextPapers = produce(papers, draft => {
  //       const index = draft.findIndex(p => (p.citedCount ?? 0) < (createdPaper.citedCount ?? 0))
  //       // eslint-disable-next-line no-param-reassign
  //       if (index !== -1) {
  //         draft.splice(index, 0, createdPaper) // 破壊的splice利用
  //       } else {
  //         draft.push(createdPaper)
  //       }
  //     })
  //     setPapers(nextPapers)
  //   },
  //   edit: editedPaper => {
  //     console.log('Paper Edited ->', editedPaper)
  //     const nextPapers = produce(papers, draft => {
  //       const index = draft.findIndex(p => p.id === editedPaper.id)
  //       // eslint-disable-next-line no-param-reassign
  //       if (index !== -1) draft[index] = editedPaper
  //     })
  //     setPapers(nextPapers)
  //   },
  //   delete: deletedPaper => {
  //     console.log('Paper Deleted ->', deletedPaper)
  //     const nextPapers = produce(papers, draft => {
  //       const index = draft.findIndex(p => p.id === deletedPaper.id)
  //       if (index !== -1) draft.splice(index, 1) // 破壊的splice利用
  //     })
  //     setPapers(nextPapers)
  //   }
  // }

  return (
    <PaperContext.Provider
      value={{
        papers: detailedPapers,
        outsidePapers: detailedOutsidePapers,
        papersMap,
        forFilter: {
          eachTagIndexes,
          ancestorTag2DIndexes,
          draftStatusFilterdPaperIds
        }
        // handler
        // handleRefetchAllPapers
      }}
    >
      {isInitLoading ? (
        <LoadingScreen />
      ) : (
        <>
          {/* {refetching && <LoadingOverlay isLoading />} */}
          {children}
        </>
      )}
      {/* isInitLoadingでSnapshotが入るのを待たないとRenderingが複数回実施されてしまう */}
    </PaperContext.Provider>
  )
}

export const usePaper = () => React.useContext(PaperContext)
